import React from 'react';
import { getCurrentLanguage } from '../../../utils/translations';
import useLanguageStore from '../../../store/languageStore';

class GoogleTagManager extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      currentLanguage: getCurrentLanguage()
    };
  }

  componentDidMount() {
    // Initialize GTM if not already loaded
    this.initializeGTM();
    
    // Set up click tracking
    this.setupClickTracking();
    
    // Set up global language variable
    this.setupGlobalLanguageVariable();
    
    // Subscribe to language changes
    this.unsubscribe = useLanguageStore.subscribe((state) => {
      this.setState({ currentLanguage: state.currentLanguage }, () => {
        this.updateGlobalLanguageVariable();
      });
    });
  }

  componentWillUnmount() {
    // Clean up event listeners
    this.removeClickTracking();
    
    // Clean up language subscription
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  }

  initializeGTM = () => {
    // Check if GTM is already loaded
    if (window.dataLayer && window.gtag) {
      return;
    }

    // Initialize dataLayer if not exists
    window.dataLayer = window.dataLayer || [];
    
    // GTM script injection (if not already present)
    if (!document.querySelector('script[src*="googletagmanager.com/gtm.js"]')) {
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://www.googletagmanager.com/gtm.js?id=GTM-52LFS5J8';
      
      const firstScript = document.getElementsByTagName('script')[0];
      firstScript.parentNode.insertBefore(script, firstScript);

      // Initialize GTM
      window.dataLayer.push({
        'gtm.start': new Date().getTime(),
        event: 'gtm.js'
      });
    }

    // Create gtag function
    window.gtag = window.gtag || function() {
      window.dataLayer.push(arguments);
    };
  };

  setupGlobalLanguageVariable = () => {
    // Set global variable for current language
    window.current_language = this.state.currentLanguage;
    
    // Also push to dataLayer for GTM
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'language_initialized',
        current_language: this.state.currentLanguage
      });
    }
  };

  updateGlobalLanguageVariable = () => {
    // Update global variable
    window.current_language = this.state.currentLanguage;
    
    // Push language change event to dataLayer
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'language_changed',
        current_language: this.state.currentLanguage,
        previous_language: window.current_language
      });
    }
  };

  setupClickTracking = () => {
    // Add global click event listener
    document.addEventListener('click', this.handleGlobalClick, true);
  };

  removeClickTracking = () => {
    document.removeEventListener('click', this.handleGlobalClick, true);
  };

  handleGlobalClick = (event) => {
    try {
      const element = event.target;
      const clickData = this.extractClickData(element, event);
      
      if (clickData) {
        this.trackClickEvent(clickData);
      }
    } catch (error) {
      console.error('❌ Error tracking click:', error);
    }
  };

  extractClickData = (element, event) => {
    // Get the closest clickable element
    const clickableElement = this.findClickableElement(element);
    if (!clickableElement) return null;

    const data = {
      element_type: clickableElement.tagName.toLowerCase(),
      element_id: clickableElement.id || null,
      element_class: clickableElement.className || null,
      element_text: this.getElementText(clickableElement),
      element_href: null,
      element_role: clickableElement.getAttribute('role') || null,
      click_coordinates: {
        x: event.clientX,
        y: event.clientY
      },
      page_url: window.location.href,
      page_title: document.title,
      timestamp: new Date().toISOString()
    };

    // Extract URL information
    if (clickableElement.tagName === 'A') {
      data.element_href = clickableElement.href;
      data.is_external_link = this.isExternalLink(clickableElement.href);
      data.link_target = clickableElement.target || '_self';
    }

    // Check if it's a button
    if (clickableElement.tagName === 'BUTTON' || clickableElement.getAttribute('role') === 'button') {
      data.button_type = clickableElement.type || 'button';
    }

    // Get component context if available
    data.component_context = this.getComponentContext(clickableElement);

    return data;
  };

  findClickableElement = (element) => {
    // Traverse up the DOM to find a clickable element
    let current = element;
    let maxDepth = 10; // Prevent infinite loops
    
    while (current && maxDepth > 0) {
      // Check if current element is clickable
      if (this.isClickableElement(current)) {
        return current;
      }
      
      current = current.parentElement;
      maxDepth--;
    }
    
    return null;
  };

  isClickableElement = (element) => {
    if (!element || !element.tagName) return false;
    
    const tag = element.tagName.toLowerCase();
    const role = element.getAttribute('role');
    const onclick = element.onclick;
    const cursor = window.getComputedStyle(element).cursor;
    
    return (
      tag === 'a' ||
      tag === 'button' ||
      tag === 'input' ||
      tag === 'select' ||
      tag === 'textarea' ||
      role === 'button' ||
      role === 'link' ||
      role === 'tab' ||
      onclick ||
      cursor === 'pointer'
    );
  };

  getElementText = (element) => {
    // Get meaningful text content
    if (!element) return '';
    
    // For images, get alt text
    if (element.tagName === 'IMG') {
      return element.alt || element.title || '';
    }
    
    // For inputs, get value or placeholder
    if (element.tagName === 'INPUT') {
      return element.value || element.placeholder || element.title || '';
    }
    
    // Get text content, but limit length
    const text = element.textContent || element.innerText || '';
    return text.trim().substring(0, 100);
  };

  isExternalLink = (href) => {
    if (!href) return false;
    
    try {
      const url = new URL(href);
      return url.hostname !== window.location.hostname;
    } catch {
      return false;
    }
  };

  getComponentContext = (element) => {
    // Try to identify which component the element belongs to
    let current = element;
    let maxDepth = 15;
    
    while (current && maxDepth > 0) {
      const classList = current.classList;
      
      if (classList) {
        // Check for common component class patterns
        for (let className of classList) {
          if (className.includes('banner')) return 'banner';
          if (className.includes('card')) return 'card';
          if (className.includes('chatbot')) return 'chatbot';
          if (className.includes('modal')) return 'modal';
          if (className.includes('highlight')) return 'highlight';
          if (className.includes('content-box')) return 'content-box';
          if (className.includes('link')) return 'link';
          if (className.includes('header')) return 'header';
          if (className.includes('navigation')) return 'navigation';
        }
      }
      
      current = current.parentElement;
      maxDepth--;
    }
    
    return 'unknown';
  };

  trackClickEvent = (data) => {
    // Add current language to the data
    const enrichedData = {
      ...data,
      current_language: window.current_language || this.state.currentLanguage
    };

    // Send to Google Tag Manager
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'click_tracking',
        event_category: 'User Interaction',
        event_action: 'Click',
        event_label: enrichedData.element_text || enrichedData.element_type,
        current_language: enrichedData.current_language,
        custom_parameters: {
          element_type: enrichedData.element_type,
          element_id: enrichedData.element_id,
          element_class: enrichedData.element_class,
          element_text: enrichedData.element_text,
          element_href: enrichedData.element_href,
          component_context: enrichedData.component_context,
          is_external_link: enrichedData.is_external_link,
          click_coordinates: enrichedData.click_coordinates,
          page_url: enrichedData.page_url,
          current_language: enrichedData.current_language,
          timestamp: enrichedData.timestamp
        }
      });
    }

    // Also send via gtag if available (backup method)
    if (window.gtag) {
      window.gtag('event', 'click', {
        event_category: 'User Interaction',
        event_label: enrichedData.element_text || enrichedData.element_type,
        current_language: enrichedData.current_language,
        custom_map: {
          'custom_element_type': enrichedData.element_type,
          'custom_component': enrichedData.component_context,
          'custom_href': enrichedData.element_href,
          'custom_language': enrichedData.current_language
        }
      });
    }
  };

  render() {
    return null; // This component doesn't render anything
  }
}

export default GoogleTagManager;